/**
 * AuthenticationDrawer Component
 */

import tr from '../../../../locales';
import { clearPostAuthenticationRequests } from '../../../../redux/actions/postAuthenticationRequests';
import { toggleLogInFormVisibility, toggleSignUpFormVisibility } from '../../../../redux/actions/ui';
import { toInnerHTML } from '../../../../utils/string';
import { encodeForUrl } from '../../../../utils/url';
import './styles.scss';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { WithRouter, withRouter } from 'found';
import { get, isEqual, template } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';

import Button from '../../../atoms/buttons/Button';
import {
  IconExternalLink,
  IconFacebook,
  IconGoogle,
} from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import Drawer, { DrawerProps } from '../../../molecules/modals/Drawer';
import { LoginPagePath, SignupPagePath } from '../../../pages/account/Login';
import EmailPasswordForm from '../AuthenticationDrawer/EmailPasswordForm';

interface IOwnStateProps {
  cognitoSession?: CognitoUserSession;
  facebookLoginUrl?: string;
  googleLoginUrl?: string;
  loginPageUrl?: string;
  signUpReason: SignUpRequestReason;
}

interface IOwnDispatchProps {
  close(action: AuthenticationDrawerAction): void;
  showLogInForm(): void;
  showSignUpForm(): void;
}

export type AuthenticationDrawerAction = 'login' | 'signup';

interface IOwnProps {
  action?: AuthenticationDrawerAction;
  className?: string;
  drawerProps?: Omit<DrawerProps, 'forwardedRef'>;
  useAbsoluteLink?: boolean;
}

interface IOwnMergedProps {
  authenticateWithEmail(cognitoSession: CognitoUserSession): void;
}

type AuthenticationDrawerProps =
  & IOwnStateProps
  & IOwnDispatchProps
  & IOwnProps
  & IOwnMergedProps;

interface IOwnState {
  isOpen: boolean;
}

const classes = new BEM('login-drawer');

class AuthenticationDrawer extends React.Component<AuthenticationDrawerProps & WithRouter, IOwnState> {
  static defaultProps: Partial<AuthenticationDrawerProps> = {
    action: 'login',
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: true,
    };
  }

  close = () => new Promise((resolve, reject) => {
    this.setState({ isOpen: false }, resolve);
  })

  onClickLogIn = () => {
    if (this.props.useAbsoluteLink) {
      this.props.router.push(LoginPagePath);
    } else {
      this.close().then(this.props.showLogInForm);
    }
  }

  onClickSignUp = () => {
    if (this.props.useAbsoluteLink) {
      this.props.router.push(SignupPagePath);
    } else {
      this.close().then(this.props.showSignUpForm);
    }
  }

  onAuthenticateWithEmail = (cognitoSession: CognitoUserSession) => {
    this.close().then(() => {
      this.props.authenticateWithEmail(cognitoSession);
    });
  }

  onClose = (action: AuthenticationDrawerAction) => {
    this.props.close(action);
  }

  render() {
    const {
      action,
      className,
      drawerProps,
      facebookLoginUrl,
      googleLoginUrl,
      match,
      signUpReason,
    } = this.props;

    const emailFormInitialValues = {
      email: '',
      password: '',
    };

    if (match.location.query.email) {
      emailFormInitialValues.email = match.location.query.email;
    }

    const hasReason = !!signUpReason;
    let introTitle: string;
    let introText: string;
    let onClickOppositeAction: () => void;

    if (action === 'login') {
      introTitle = tr.login.intro.title;
      introText = tr.login.intro.text;
      onClickOppositeAction = this.onClickSignUp;
    } else {
      // introTitle = tr.signup.intro[signUpReason].title;
      introTitle = get(tr, `signup.intro.${signUpReason}.title`, tr.signup.intro.default.title);
      introText = get(tr, `signup.intro.${signUpReason}.text`, tr.signup.intro.default.text);
      onClickOppositeAction = this.onClickLogIn;
    }

    const translations = tr[action!];

    return (
      <Drawer
        closeButtonLabel={hasReason ? 'Cancel' : undefined}
        onClose={() => this.onClose(action!)}
        open={this.state.isOpen}
        position="right"
        {...drawerProps}
        {...classes('', '', className) }
      >
        <div {...classes('intro', '', 'drawer__content-group') }>
          <Heading is="h4"><span dangerouslySetInnerHTML={toInnerHTML(introTitle)} /></Heading>
          {introText && <p dangerouslySetInnerHTML={toInnerHTML(introText)} />}
        </div>
        <div
          {...classes('email-method', '', 'drawer__content-group') }
          data-title={translations.userPasswordMethodTitle}
        >
          <EmailPasswordForm
            key={`${action}-form`}
            form={`${action}-form`}
            action={action!}
            initialValues={emailFormInitialValues}
            onClickOppositeAction={onClickOppositeAction}
            onSubmitSuccess={this.onAuthenticateWithEmail}
            onForgetPassword={() => this.close()}
          />
        </div>
        {(!!googleLoginUrl || !!facebookLoginUrl) && (
          <div {...classes('methods', '', 'drawer__button-list') } data-title={translations.methodsTitle}>
            {googleLoginUrl && (
              <Button
                className="drawer__button-list__button"
                icon={<IconGoogle />}
                label={translations.googleLabel}
                hyperLinkProps={{ href: googleLoginUrl }}
                wrapperComponent="a"
              >
                <IconExternalLink />
              </Button>
            )}
            {facebookLoginUrl && (
              <Button
                className="drawer__button-list__button"
                icon={<IconFacebook />}
                label={translations.facebookLabel}
                hyperLinkProps={{ href: facebookLoginUrl }}
                wrapperComponent="a"
              >
                <IconExternalLink />
              </Button>
            )}
          </div>
        )}
      </Drawer>
    );
  }
}

const ConnectedAuthenticationDrawer =
  connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & WithRouter, AuthenticationDrawerProps, IStoreState>(
    ({ authentication, postAuthenticationRequests, ui, user }) => {

      let facebookLoginUrl = authentication.facebookLoginUrl;
      let googleLoginUrl = authentication.googleLoginUrl;
      // tslint:disable-next-line:no-invalid-template-strings
      let loginPageUrl = LoginPagePath + '#id_token=${idToken}&access_token=${accessToken}';

      if (postAuthenticationRequests.length) {
        const state = encodeForUrl({
          stateType: 'PostAuthenticationRequests',
          postAuthenticationRequests,
        });
        facebookLoginUrl += `&state=${state}`;
        googleLoginUrl += `&state=${state}`;
        loginPageUrl += `&state=${state}`;
      }

      return {
        cognitoSession: user.cognitoSession,
        facebookLoginUrl,
        googleLoginUrl,
        loginPageUrl,
        signUpReason: ui.signUpForm && ui.signUpForm.reason || 'default',
      };
    },
    (dispatch) => ({
      close: (which) => {
        const action = which === 'login' ? toggleLogInFormVisibility : toggleSignUpFormVisibility;
        dispatch(clearPostAuthenticationRequests());
        dispatch(action(false));
      },
      showLogInForm: () => {
        dispatch(toggleLogInFormVisibility(true));
      },
      showSignUpForm: () => {
        dispatch(toggleSignUpFormVisibility(true));
      },
    }),
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      authenticateWithEmail: (cognitoSession) => {
        const idToken = cognitoSession.getIdToken().getJwtToken();
        const accessToken = cognitoSession.getAccessToken().getJwtToken();
        const routePath = template(stateProps.loginPageUrl)({
          idToken,
          accessToken,
        });

        ownProps.router.push(routePath);
      },
    }),
    {
      areStatesEqual: (nextState, prevState) => {
        return isEqual(nextState.authentication, prevState.authentication);
      },
    },
  )(AuthenticationDrawer);

const AuthenticationDrawerWithRouter = withRouter<IOwnProps>(ConnectedAuthenticationDrawer);

export default AuthenticationDrawerWithRouter;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/modals/AuthenticationDrawer/index.tsx